import { useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useIsNewEntry } from "src/hooks";
import { useSelector } from "src/redux/hooks";

import { generatorStore, useGeneratorStore } from "../../store";
import { serializeLogbookForBackend } from "../../utils/serializeLogbook";

export function getResolvedIdType(
  idType: string | undefined,
): "lbeid" | "partid" | "meid" {
  if (idType === "partid") return "partid";
  if (idType === "meid") return "meid";
  return "lbeid";
}

export const useEntryParams = () => {
  const { logbookId, idType, lbeidOrPartId } = useParams();
  const [searchParams] = useSearchParams();
  const isNewEntry = useIsNewEntry();

  const entryIdType = getResolvedIdType(idType);

  const { yachtid, _prename, entrydate } = useSelector(
    state => state.generatorSlice.entryDataToSave,
  );
  const latestEntryId = useSelector(
    state => state.generatorSlice.latestEntry.id,
  );
  const lbName = useSelector(state => state.generatorSlice.latestEntry.lbName);
  const current = useSelector(state => state.entrySlice.current);
  const editing = useSelector(state => state.entrySlice.editing);
  const isAutoSaving = useSelector(state => state.entrySlice.isSaving);
  const isReadOnly = useSelector(
    state => state.generatorSlice.lbentry.isReadonly,
  );
  const isV2 = useSelector(
    state => state.generatorSlice.lbentry.generator === "v2",
  );
  const isDirty = useGeneratorStore(s => s.isDirty);

  const getSerializedV2Entry = useCallback(() => {
    const { refData, values } = generatorStore.getState();
    if (!refData) return null;
    return JSON.stringify(serializeLogbookForBackend(refData, values));
  }, []);

  const getRouteEntryId = useCallback(() => {
    const isNewParam = searchParams.get("new") === "true";
    const isOfflineNew = lbeidOrPartId?.includes("new=");
    const isExplicitNew =
      isNewEntry &&
      (isNewParam || latestEntryId === "new" || !lbeidOrPartId);

    if (isExplicitNew) {
      return "new";
    }
    if (isOfflineNew) {
      return lbeidOrPartId;
    }
    if (isNewEntry) {
      return "new";
    }

    return lbeidOrPartId ?? latestEntryId;
  }, [isNewEntry, lbeidOrPartId, latestEntryId, searchParams]);

  const getCommonParams = useCallback(() => {
    const lbentry = getSerializedV2Entry();
    if (!lbentry || !logbookId) return null;

    return {
      lbentry,
      lbid: logbookId,
      yachtid,
      _prename,
      entrydate,
    };
  }, [entrydate, getSerializedV2Entry, logbookId, yachtid, _prename]);

  return {
    logbookId,
    entryIdType,
    lbeidOrPartId,
    isNewEntry,
    yachtid,
    _prename,
    entrydate,
    latestEntryId,
    lbName,
    current,
    editing,
    isAutoSaving,
    isReadOnly,
    isV2,
    isDirty,
    getRouteEntryId,
    getCommonParams,
  };
};
